import React from 'react';
import { UploadCloud, Image, Video, Music, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'src/lib/utils';

type Props = {
  text?: string;
  children?: React.ReactNode;
  isActive?: boolean;
  showFileTypes?: boolean;
  showHint?: boolean;
};

const DropzonePlaceholder: React.FC<Props> = ({
  text,
  children,
  isActive = false,
  showFileTypes = true,
  showHint = true,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'tw-absolute tw-flex tw-h-full tw-w-full tw-select-none tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-transition-all tw-duration-200',
        isActive && 'tw-bg-brand-50 tw-scale-105',
        showFileTypes && showHint ? 'tw-gap-6' : 'tw-gap-2',
      )}
    >
      {/* Main Upload Icon */}
      <div
        className={cn(
          'tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-100 tw-transition-all tw-duration-200',
          isActive && 'tw-scale-110 tw-bg-brand-200',
        )}
      >
        <UploadCloud
          className={cn(
            'tw-text-brand-600 tw-transition-colors tw-duration-200',
            isActive && 'tw-text-brand-700',
          )}
          size={36}
        />
      </div>

      {/* Main Text */}
      <div className="tw-space-y-2 tw-text-center">
        <h3
          className={cn(
            'tw-text-xl tw-font-semibold tw-text-gray-900 tw-transition-colors tw-duration-200',
            isActive && 'tw-text-brand-700',
          )}
        >
          {text || t('actions:file_upload', { defaultValue: 'Upload your media files' })}
        </h3>
        <p className="tw-text-sm tw-text-gray-600">
          Drag and drop files here, or{' '}
          <span className="tw-font-medium tw-text-brand-600 tw-underline tw-decoration-dashed tw-underline-offset-2">
            click to browse
          </span>
        </p>
      </div>

      {/* Supported File Types */}
      {showFileTypes && (
        <>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4">
            <div className="tw-flex tw-items-center tw-space-x-2 tw-text-sm tw-text-gray-600">
              <Image className="tw-h-4 tw-w-4 tw-text-blue-500" />
              <span>Images</span>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-2 tw-text-sm tw-text-gray-600">
              <Video className="tw-h-4 tw-w-4 tw-text-purple-500" />
              <span>Videos</span>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-2 tw-text-sm tw-text-gray-600">
              <Music className="tw-h-4 tw-w-4 tw-text-green-500" />
              <span>Audio</span>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-2 tw-text-sm tw-text-gray-600">
              <FileText className="tw-h-4 tw-w-4 tw-text-red-500" />
              <span>Documents</span>
            </div>
          </div>
          <p className="text-center tw-text-xs tw-text-gray-500">
            PNG, JPG, GIF, SVG, MP4, MOV, MP3, WAV, PDF, DOC, XLS, PPT, TXT, CSV, ZIP, OTF, TTF, WOFF and more
          </p>
        </>
      )}

      {/* Upload Hint */}
      {showHint && (
        <div className="tw-rounded-lg tw-bg-gray-50 tw-px-4 tw-py-2 tw-text-xs tw-text-gray-600">
          <span className="tw-font-medium">Tip:</span>{' '}
          {t('components:upload.hint', { defaultValue: 'You can upload multiple files at once' })}
        </div>
      )}

      {children}
    </div>
  );
};

export { DropzonePlaceholder };
